import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, ListChecks } from 'lucide-react';
import {
  FilterChips,
  Sheet,
  SheetStat,
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceNotice,
  type FilterChipOption,
} from '@/components/ui/workspace';
import { AiSurface } from '@/components/ui/ai-surface';
import { Spinner } from '@/components/ui/spinner';
import { UploadProgressBar, uploadPercent } from '@/components/ui/upload-progress';
import { StageProgressBar } from '@/components/ui/stage-progress';
import { cn } from '@/lib/utils/cn';
import { ApiError } from '@/lib/api/client';
import {
  candidateRefusalFromError,
  confirmIntakeRun,
  intakeConflictReason,
  intakeKeys,
  intakePollDelayMs,
  isBudgetExhausted,
  isRunInProgress,
  repeatFromError,
  rereadIntakeDocument,
  runInProgressFromError,
  startIntakeRun,
  useBulkAcceptMutation,
  useCancelRunMutation,
  useIntakeCandidatesQuery,
  useIntakeRunQuery,
  usePatchCandidateMutation,
  type IntakeCandidate,
  type IntakeConfirmResult,
  type IntakeRun,
  type RepeatDocument,
} from '@/lib/api/requirement-library-intake';
import type { UploadProgress } from '@/lib/api/upload';
import { useUsersLookupQuery } from '@/lib/api/users-lookup';
import { nudgeIntakeWatch, useIntakeRunOnScreen } from '@/lib/stores/intake-watch';
import { plural } from '../library-logic';
import { IntakeCandidateCard } from './intake-candidate-card';
import { IntakeSourceViewer, type SourceTarget } from './intake-source-viewer';
import {
  INTAKE_ACCEPT,
  candidateCounts,
  formatCost,
  formatWhen,
  intakeFileProblem,
  isRecoveryFailure,
  matchesCandidateFilter,
  pageCountWords,
  refusalMessage,
  runCountsLine,
  runFailureMessage,
  runProgressLine,
  runReadingProgress,
  runStatusLabel,
  runWaitingNotice,
  type CandidateFilter,
} from './intake-logic';

/**
 * "Import from a document" sheet (T-0154; E11-F08).
 *
 * Reference: `openAiImport` / `renderAi` in `docs/requirement-library/
 * prototype/selection.js`. One surface with visible stages:
 *
 *   drop     the confidentiality warning, shown BEFORE a file is chosen
 *            (scenario 9), and the note that it runs on the firm AI budget
 *   reading  the run's status, polled, on an AI surface, with cancel
 *   review   one card per candidate, a summary with the bulk accept, the
 *            filters, and the footer counts with "Add N to drafts"
 *
 * and the states a run can end in: nothing found (scenario 8), failed,
 * budget exhausted (scenario 7), the same document again (scenario 10),
 * cancelled, and confirmed (read-only).
 *
 * Nothing reaches a draft before the confirm. The sheet can be closed at
 * any point: the run stays on the server and is reopened from "Document
 * imports" (`initialRunId`).
 */

export interface IntakeSheetProps {
  /** Reopen this run instead of starting at the drop stage. */
  initialRunId?: string | null;
  /** Sub-categories a module already has, for the sub-category field. */
  subcategoriesFor?: (moduleId: string) => ReadonlyArray<string>;
  onClose: () => void;
  /** The run was confirmed: accepted candidates are in their modules' drafts. */
  onConfirmed: (result: IntakeConfirmResult) => void;
  /** Open the list of past and in-progress imports. */
  onOpenRuns?: () => void;
  /** Delay before each poll of a run in progress. Tests shorten it. */
  pollDelay?: (attempt: number) => number;
}

type View =
  | { kind: 'drop'; problem: string | null }
  | {
      kind: 'uploading';
      fileName: string;
      /** Bytes sent; null before the first progress event (T-0156). */
      progress: UploadProgress | null;
      /** Every byte has been sent; the server has not answered yet. */
      processing: boolean;
    }
  | { kind: 'repeat'; repeat: RepeatDocument; error: string | null; busy: boolean }
  | { kind: 'budget'; message: string }
  | { kind: 'run'; runId: string };

const NO_SUBCATEGORIES: ReadonlyArray<string> = [];
const BUDGET_FALLBACK =
  'The firm AI budget for this month is used up, so the document was not read.';

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function newKey(): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Why it stopped and who can change that (scenario 7). */
function BudgetNotice({ message }: { message: string }) {
  return (
    <WorkspaceNotice tone="at-risk">
      <div className="font-semibold text-ink-900">The firm AI budget for this month is used up</div>
      <p className="mt-0.5">{message}</p>
      <p className="mt-1.5">
        Nothing was added. The monthly cap is changed by a firm admin in{' '}
        <a
          href="/settings/ai-costs"
          className="font-medium text-ink-900 underline underline-offset-[3px]"
        >
          AI cost governance
        </a>
        .
      </p>
    </WorkspaceNotice>
  );
}

export function IntakeSheet({
  initialRunId = null,
  subcategoriesFor,
  onClose,
  onConfirmed,
  onOpenRuns,
  pollDelay = intakePollDelayMs,
}: IntakeSheetProps) {
  const qc = useQueryClient();
  const [view, setView] = React.useState<View>(
    initialRunId ? { kind: 'run', runId: initialRunId } : { kind: 'drop', problem: null },
  );
  const [dragOver, setDragOver] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  /** One key per chosen file, reused if the same upload is retried. */
  const uploadKey = React.useRef<{ file: File; key: string } | null>(null);
  /** The upload in flight; "Cancel upload" aborts it. */
  const uploadAbort = React.useRef<AbortController | null>(null);

  const runId = view.kind === 'run' ? view.runId : null;
  const runQuery = useIntakeRunQuery(runId, pollDelay);
  // T-0157: this sheet says how the run ends, so the app-wide watcher stays quiet about it.
  useIntakeRunOnScreen(runId);
  const run = runQuery.data ?? null;
  const reviewable = run?.status === 'ready_for_review';
  /** Read to the end with nothing to show (scenario 8): there is no list to ask for. */
  const noneFound =
    reviewable && (run?.outcome === 'none_found' || (run?.counts.candidates ?? 0) === 0);
  const closedWithCandidates = run?.status === 'confirmed';
  const candidatesQuery = useIntakeCandidatesQuery(
    runId,
    Boolean((reviewable && !noneFound) || closedWithCandidates),
  );
  const listing = candidatesQuery.data ?? null;
  const candidates = React.useMemo(() => listing?.candidates ?? [], [listing]);
  const counts = React.useMemo(() => candidateCounts(candidates), [candidates]);

  const patchMutation = usePatchCandidateMutation();
  const bulkMutation = useBulkAcceptMutation();
  const cancelMutation = useCancelRunMutation();

  const [filter, setFilter] = React.useState<CandidateFilter>('all');
  const [saving, setSaving] = React.useState<ReadonlySet<string>>(new Set());
  const [cardErrors, setCardErrors] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [reviewError, setReviewError] = React.useState<string | null>(null);
  const [flaggedId, setFlaggedId] = React.useState<string | null>(null);
  const [confirming, setConfirming] = React.useState(false);
  const [rereading, setRereading] = React.useState(false);
  const [discardOpen, setDiscardOpen] = React.useState(false);
  const [source, setSource] = React.useState<SourceTarget | null>(null);
  const confirmKey = React.useRef<string | null>(null);

  React.useEffect(() => {
    // Another run is another review.
    setFilter('all');
    setSaving(new Set());
    setCardErrors(new Map());
    setReviewError(null);
    setFlaggedId(null);
    confirmKey.current = null;
  }, [runId]);

  const moduleOptions = React.useMemo(
    () =>
      (listing?.modules ?? [])
        .filter((m) => m.status == null || m.status === 'active')
        .map((m) => ({ id: m.id, name: m.name })),
    [listing],
  );

  // ---- starting a run -------------------------------------------------------
  const openRun = React.useCallback(
    (next: IntakeRun) => {
      qc.setQueryData(intakeKeys.run(next.id), next);
      void qc.invalidateQueries({ queryKey: intakeKeys.runs() });
      // T-0157: the app-wide watcher follows it after this sheet is closed.
      nudgeIntakeWatch(next.id);
      setView({ kind: 'run', runId: next.id });
    },
    [qc],
  );

  const upload = (file: File) => {
    const problem = intakeFileProblem(file);
    if (problem) {
      setView({ kind: 'drop', problem });
      return;
    }
    if (uploadKey.current?.file !== file) uploadKey.current = { file, key: newKey() };
    uploadAbort.current?.abort();
    const controller = new AbortController();
    uploadAbort.current = controller;
    const live = () => !controller.signal.aborted;
    setView({ kind: 'uploading', fileName: file.name, progress: null, processing: false });
    startIntakeRun(file, uploadKey.current.key, controller.signal, {
      onProgress: (progress) => {
        if (!live()) return;
        setView((prev) =>
          prev.kind !== 'uploading' ||
          prev.processing ||
          (prev.progress && uploadPercent(prev.progress) === uploadPercent(progress))
            ? prev
            : { ...prev, progress },
        );
      },
      onUploaded: () => {
        if (!live()) return;
        setView((prev) => (prev.kind === 'uploading' ? { ...prev, processing: true } : prev));
      },
    }).then(
      (next) => {
        if (!live()) return;
        uploadAbort.current = null;
        openRun(next);
      },
      (err: unknown) => {
        // Cancelled: `cancelUpload` has already gone back to the drop stage.
        if (!live()) return;
        uploadAbort.current = null;
        const repeat = repeatFromError(err);
        if (repeat) {
          setView({ kind: 'repeat', repeat, error: null, busy: false });
        } else if (isBudgetExhausted(err)) {
          setView({ kind: 'budget', message: messageOf(err, BUDGET_FALLBACK) });
        } else if (intakeConflictReason(err) === 'document_withdrawn') {
          setView({
            kind: 'drop',
            problem: `${file.name} was uploaded before and then withdrawn by a firm admin. It cannot be read again.`,
          });
        } else {
          setView({
            kind: 'drop',
            problem: `${file.name} was not read: ${messageOf(err, 'the upload failed.')}`,
          });
        }
      },
    );
  };

  /** Abort the upload in flight and go back to the drop stage. */
  const cancelUpload = () => {
    uploadAbort.current?.abort();
    uploadAbort.current = null;
    setView({ kind: 'drop', problem: null });
  };

  /** Read a stored document again; `onError` puts a refusal where the request came from. */
  const reread = (documentId: string, onError: (message: string) => void): Promise<void> =>
    rereadIntakeDocument(documentId).then(openRun, (err: unknown) => {
      const inProgress = runInProgressFromError(err);
      if (inProgress) openRun(inProgress);
      else if (isBudgetExhausted(err)) {
        setView({ kind: 'budget', message: messageOf(err, BUDGET_FALLBACK) });
      } else if (intakeConflictReason(err) === 'document_withdrawn') {
        onError('This document was withdrawn by a firm admin and cannot be read again.');
      } else onError(messageOf(err, 'The document could not be read again.'));
    });

  const rereadFromRepeat = () => {
    if (view.kind !== 'repeat') return;
    const repeat = view.repeat;
    setView({ kind: 'repeat', repeat, error: null, busy: true });
    void reread(repeat.document.id, (message) =>
      setView({ kind: 'repeat', repeat, error: message, busy: false }),
    );
  };

  const rereadFromRun = () => {
    if (!run) return;
    setRereading(true);
    setReviewError(null);
    void reread(run.document.id, setReviewError).finally(() => setRereading(false));
  };

  // ---- deciding -------------------------------------------------------------
  const change = (
    candidate: IntakeCandidate,
    input: { decision?: 'accept' | 'skip' | 'undecided'; moduleId?: string; subcategory?: string },
  ) => {
    if (!runId) return;
    setSaving((prev) => new Set(prev).add(candidate.id));
    setCardErrors((prev) => {
      const next = new Map(prev);
      next.delete(candidate.id);
      return next;
    });
    if (flaggedId === candidate.id) {
      setFlaggedId(null);
      setReviewError(null);
    }
    patchMutation
      .mutateAsync({ runId, candidateId: candidate.id, ...input })
      .catch((err: unknown) => {
        if (intakeConflictReason(err) === 'run_not_reviewable') {
          void runQuery.refetch();
          return;
        }
        setCardErrors((prev) =>
          new Map(prev).set(
            candidate.id,
            err instanceof ApiError && err.details?.['module_id'] === 'required'
              ? 'Choose a module before accepting this candidate.'
              : messageOf(err, 'The change was not saved.'),
          ),
        );
      })
      .finally(() =>
        setSaving((prev) => {
          const next = new Set(prev);
          next.delete(candidate.id);
          return next;
        }),
      );
  };

  const bulkAccept = () => {
    if (!runId) return;
    setReviewError(null);
    bulkMutation.mutate(runId, {
      onError: (err) => {
        if (intakeConflictReason(err) === 'run_not_reviewable') void runQuery.refetch();
        else setReviewError(messageOf(err, 'The candidates could not be accepted.'));
      },
    });
  };

  const confirm = () => {
    if (!runId || confirming || counts.accepted === 0) return;
    if (!confirmKey.current) confirmKey.current = newKey();
    setConfirming(true);
    setReviewError(null);
    setFlaggedId(null);
    confirmIntakeRun(runId, confirmKey.current).then(
      (result) => {
        setConfirming(false);
        void qc.invalidateQueries({ queryKey: intakeKeys.all });
        onConfirmed(result);
      },
      (err: unknown) => {
        setConfirming(false);
        // The server answered: the next confirm is a new attempt. Only a
        // lost response is retried under the same key.
        if (!(err instanceof ApiError) || err.code !== 'network') confirmKey.current = null;
        const refusal = candidateRefusalFromError(err);
        const reason = intakeConflictReason(err);
        if (refusal) {
          setFlaggedId(refusal.candidateId);
          setFilter('all');
          setReviewError(refusalMessage(refusal));
        } else if (reason === 'nothing_accepted') {
          setReviewError('Accept at least one candidate, then confirm.');
          void candidatesQuery.refetch();
        } else if (reason === 'document_withdrawn') {
          setReviewError(
            'This document was withdrawn by a firm admin, so nothing can be added from it.',
          );
        } else if (reason === 'run_not_reviewable') {
          void runQuery.refetch();
        } else {
          setReviewError(messageOf(err, 'Nothing was added. Try again.'));
        }
      },
    );
  };

  const cancelRun = () => {
    if (!runId) return;
    setReviewError(null);
    cancelMutation.mutate(runId, {
      onSettled: () => setDiscardOpen(false),
      onError: (err) => {
        if (intakeConflictReason(err) === 'run_closed') void runQuery.refetch();
        else setReviewError(messageOf(err, 'The import could not be cancelled.'));
      },
    });
  };

  // ---- derived --------------------------------------------------------------
  const inProgress = run != null && isRunInProgress(run.status);
  const inReview = reviewable === true && !noneFound;
  const shown = candidates.filter((c) => matchesCandidateFilter(c, filter));
  const filterOptions: FilterChipOption<CandidateFilter>[] = [
    { value: 'all', label: 'All', count: counts.total },
    { value: 'new', label: 'New', count: counts.fresh },
    { value: 'duplicates', label: 'Possible duplicates', count: counts.duplicates },
    { value: 'low', label: 'Low confidence', count: counts.lowConfidence },
  ];
  const repeatRun = view.kind === 'repeat' ? view.repeat.previousRun : null;
  const names = useUsersLookupQuery(repeatRun?.started_by ? [repeatRun.started_by] : []).data
    ?.items;
  const startedByName = repeatRun?.started_by
    ? (names?.find((u) => u.id === repeatRun.started_by)?.display_name ?? null)
    : null;

  const startOver = () => {
    uploadKey.current = null;
    setView({ kind: 'drop', problem: null });
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (view.kind !== 'drop') return;
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  // ---- body -----------------------------------------------------------------
  let body: React.ReactNode;
  if (view.kind === 'drop') {
    body = (
      <div
        data-testid="intake-drop"
        className={cn(
          'flex flex-1 flex-col items-center justify-center gap-3 rounded-large border-[1.5px] border-dashed p-10 text-center',
          dragOver ? 'border-accent-amber-600 bg-accent-amber-50' : 'border-ink-200 bg-paper-0',
        )}
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-card bg-paper-100 text-ink-700">
          <FileText className="h-[18px] w-[18px]" aria-hidden="true" />
        </span>
        <div className="text-h3 font-semibold text-ink-900">
          Drop an RFP, requirements appendix or client list
        </div>
        <p className="max-w-[480px] text-meta font-medium text-ink-500">
          PDF, Word or Excel, up to 50 MB. Each requirement found is checked against the library for
          duplicates, given a suggested module, and linked back to the passage it came from.
        </p>
        <WorkspaceNotice tone="at-risk" className="max-w-[520px] text-left">
          The document is stored in the firm library so citations keep working, and every curator
          can open it. Do not upload material a client has asked to keep confidential.
        </WorkspaceNotice>
        <WorkspaceButton size="md" variant="primary" onClick={() => inputRef.current?.click()}>
          Choose a document
        </WorkspaceButton>
        <p className="text-meta text-ink-500">
          Runs against the firm AI budget. Nothing is added to the library until you accept it.
        </p>
        {view.problem ? (
          <WorkspaceNotice tone="critical" className="max-w-[520px] text-left">
            {view.problem}
          </WorkspaceNotice>
        ) : null}
      </div>
    );
  } else if (view.kind === 'uploading') {
    body = (
      <AiSurface
        role="status"
        className="flex flex-1 flex-col items-center justify-center gap-2 rounded-large p-10 text-center"
      >
        <Spinner />
        <div className="text-h3 font-semibold text-ai-ink">Sending {view.fileName}…</div>
        <p className="text-meta font-medium text-ink-500">
          The document is being stored in the firm library.
        </p>
        <UploadProgressBar
          className="max-w-[360px]"
          fileName={view.fileName}
          progress={view.progress}
          processing={view.processing}
        />
        <WorkspaceButton className="mt-2" onClick={cancelUpload}>
          Cancel upload
        </WorkspaceButton>
      </AiSurface>
    );
  } else if (view.kind === 'budget') {
    body = (
      <>
        <BudgetNotice message={view.message} />
        <div>
          <WorkspaceButton onClick={startOver}>Choose another document</WorkspaceButton>
        </div>
      </>
    );
  } else if (view.kind === 'repeat') {
    const previous = view.repeat.previousRun;
    body = (
      <>
        <WorkspaceNotice tone="at-risk">
          <div className="font-semibold text-ink-900">This document was processed before</div>
          <p className="mt-0.5">
            The same content was uploaded as{' '}
            <span className="font-mono text-meta">{view.repeat.document.file_name}</span> on{' '}
            {formatWhen(previous.created_at)}
            {startedByName ? ` by ${startedByName}` : ''}. Outcome: {runStatusLabel(previous)}
            {' · '}
            {runCountsLine(previous)}.
          </p>
          <p className="mt-1.5">
            Open that import, or read the document again. If you read it again, every candidate that
            was added last time is flagged as already in the library.
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            <WorkspaceButton
              variant="primary"
              disabled={view.busy}
              onClick={() => setView({ kind: 'run', runId: previous.id })}
            >
              Open the earlier import
            </WorkspaceButton>
            <WorkspaceButton disabled={view.busy} onClick={rereadFromRepeat}>
              {view.busy ? 'Starting…' : 'Read the document again'}
            </WorkspaceButton>
            <WorkspaceButton variant="tertiary" disabled={view.busy} onClick={startOver}>
              Choose another document
            </WorkspaceButton>
          </div>
        </WorkspaceNotice>
        {view.error ? <WorkspaceNotice tone="critical">{view.error}</WorkspaceNotice> : null}
      </>
    );
  } else if (runQuery.isPending) {
    body = (
      <div
        role="status"
        className="flex flex-1 items-center justify-center gap-2 text-body-sm text-ink-500"
      >
        <Spinner /> Loading the import…
      </div>
    );
  } else if (runQuery.isError || !run) {
    body = (
      <WorkspaceNotice tone="critical">
        <div className="font-semibold text-ink-900">The import could not be loaded</div>
        <p className="mt-0.5">{messageOf(runQuery.error, '')}</p>
        <div className="mt-2 flex gap-2">
          <WorkspaceButton onClick={() => void runQuery.refetch()}>Try again</WorkspaceButton>
          <WorkspaceButton variant="tertiary" onClick={startOver}>
            Choose another document
          </WorkspaceButton>
        </div>
      </WorkspaceNotice>
    );
  } else if (inProgress) {
    const waitingNotice = runWaitingNotice(run, Date.now());
    // T-0159: sections read over sections built, from the run's own counts.
    const readingProgress = runReadingProgress(run);
    body = (
      <>
        <AiSurface
          role="status"
          aria-label="Import in progress"
          className="flex flex-1 flex-col items-center justify-center gap-2 rounded-large p-10 text-center"
        >
          <Spinner />
          <div className="text-h3 font-semibold text-ai-ink">Reading {run.document.file_name}…</div>
          <p className="text-meta font-medium text-ink-700">{runStatusLabel(run)}</p>
          {readingProgress ? (
            <StageProgressBar
              className="w-full max-w-[360px]"
              percent={readingProgress.percent}
              label={readingProgress.label}
              ariaLabel={`Reading ${run.document.file_name}`}
              tone="ai"
              size="md"
            />
          ) : null}
          <p className="max-w-[480px] text-meta font-medium text-ink-500">{runProgressLine(run)}</p>
          <WorkspaceButton className="mt-2" disabled={cancelMutation.isPending} onClick={cancelRun}>
            {cancelMutation.isPending ? 'Cancelling…' : 'Cancel this import'}
          </WorkspaceButton>
          <p className="max-w-[480px] text-meta text-ink-500">
            You can close this sheet. The document keeps being read, and the import is listed under
            Document imports when it is ready for review.
          </p>
        </AiSurface>
        {waitingNotice ? <WorkspaceNotice>{waitingNotice}</WorkspaceNotice> : null}
        {reviewError ? <WorkspaceNotice tone="critical">{reviewError}</WorkspaceNotice> : null}
      </>
    );
  } else if (run.status === 'failed') {
    const budget = run.error?.code === 'ai.budget_exhausted';
    body = (
      <>
        {budget ? (
          <BudgetNotice message={runFailureMessage(run)} />
        ) : (
          <WorkspaceNotice tone="critical">
            <div className="font-semibold text-ink-900">
              {run.document.file_name} could not be read
            </div>
            <p className="mt-0.5">{runFailureMessage(run)}</p>
            {run.error?.code ? (
              <p className="mt-1 font-mono text-meta text-ink-500">{run.error.code}</p>
            ) : null}
          </WorkspaceNotice>
        )}
        <div className="flex flex-wrap gap-2">
          {run.error?.code !== 'document_withdrawn' && !run.document.withdrawn ? (
            <WorkspaceButton variant="primary" disabled={rereading} onClick={rereadFromRun}>
              {rereading ? 'Starting…' : isRecoveryFailure(run) ? 'Start again' : 'Try again'}
            </WorkspaceButton>
          ) : null}
          <WorkspaceButton onClick={startOver}>Choose another document</WorkspaceButton>
        </div>
        {reviewError ? <WorkspaceNotice tone="critical">{reviewError}</WorkspaceNotice> : null}
      </>
    );
  } else if (run.status === 'cancelled') {
    body = (
      <>
        <WorkspaceNotice>
          <div className="font-semibold text-ink-900">This import was cancelled</div>
          <p className="mt-0.5">
            Nothing was added from {run.document.file_name}. The document is still stored and can be
            read again.
          </p>
        </WorkspaceNotice>
        <div className="flex flex-wrap gap-2">
          {!run.document.withdrawn ? (
            <WorkspaceButton variant="primary" disabled={rereading} onClick={rereadFromRun}>
              {rereading ? 'Starting…' : 'Read the document again'}
            </WorkspaceButton>
          ) : null}
          <WorkspaceButton onClick={startOver}>Choose another document</WorkspaceButton>
        </div>
        {reviewError ? <WorkspaceNotice tone="critical">{reviewError}</WorkspaceNotice> : null}
      </>
    );
  } else if (noneFound) {
    body = (
      <>
        <AiSurface role="status" className="rounded-card px-4 py-3 text-body-sm">
          <div className="font-semibold text-ai-ink">
            No requirements were found in {run.document.file_name}
          </div>
          <p className="mt-0.5 text-ink-700">
            The whole document was read
            {pageCountWords(run.counts.pages, run.document.page_source)
              ? ` (${pageCountWords(run.counts.pages, run.document.page_source)})`
              : ''}{' '}
            and no requirement statements were found. Nothing was added, and nothing is charged
            beyond the read
            {run.cost_cents != null ? ` (${formatCost(run.cost_cents)})` : ''}.
          </p>
        </AiSurface>
        <div className="flex flex-wrap gap-2">
          <WorkspaceButton
            variant="primary"
            disabled={cancelMutation.isPending}
            onClick={cancelRun}
          >
            {cancelMutation.isPending ? 'Closing…' : 'Close this import'}
          </WorkspaceButton>
          <WorkspaceButton onClick={startOver}>Choose another document</WorkspaceButton>
        </div>
        {reviewError ? <WorkspaceNotice tone="critical">{reviewError}</WorkspaceNotice> : null}
      </>
    );
  } else if (candidatesQuery.isPending) {
    body = (
      <div
        role="status"
        className="flex flex-1 items-center justify-center gap-2 text-body-sm text-ink-500"
      >
        <Spinner /> Loading the candidates…
      </div>
    );
  } else if (candidatesQuery.isError) {
    body = (
      <WorkspaceNotice tone="critical">
        The candidates could not be loaded. {messageOf(candidatesQuery.error, '')}{' '}
        <button
          type="button"
          className="font-medium underline underline-offset-[3px]"
          onClick={() => void candidatesQuery.refetch()}
        >
          Try again
        </button>
      </WorkspaceNotice>
    );
  } else {
    const failedWindows = run.counts.failed_windows ?? 0;
    body = (
      <>
        <AiSurface
          role="status"
          aria-label="Import summary"
          className="flex flex-wrap items-center gap-3 rounded-card px-4 py-3 text-body-sm"
        >
          <div className="min-w-0 flex-1 text-ink-700">
            {inReview ? (
              <>
                <strong className="font-semibold text-ai-ink">
                  {plural(counts.total, 'candidate requirement')} found in {run.document.file_name}.
                </strong>{' '}
                {counts.duplicates.toLocaleString()}{' '}
                {counts.duplicates === 1 ? 'looks like a requirement' : 'look like requirements'}{' '}
                already in the library and {counts.lowConfidence.toLocaleString()}{' '}
                {counts.lowConfidence === 1 ? 'is' : 'are'} low confidence. Nothing is added until
                you accept it, and accepted items go to a private draft first.
              </>
            ) : (
              <>
                <strong className="font-semibold text-ai-ink">
                  This import was confirmed on {formatWhen(run.closed_at)}.
                </strong>{' '}
                {plural(counts.accepted, 'candidate')} of {counts.total.toLocaleString()} went to
                module drafts. It is shown here as it was decided and cannot be changed.
              </>
            )}
          </div>
          {inReview ? (
            <WorkspaceButton
              className="flex-none"
              disabled={counts.bulkAcceptable === 0 || bulkMutation.isPending || confirming}
              title={
                counts.bulkAcceptable === 0
                  ? 'Every new, confident candidate has been decided'
                  : 'Possible duplicates and low-confidence candidates are left for you to decide one by one'
              }
              onClick={bulkAccept}
            >
              <ListChecks className="h-3.5 w-3.5" aria-hidden="true" />
              {bulkMutation.isPending
                ? 'Accepting…'
                : `Accept the ${counts.bulkAcceptable.toLocaleString()} new, confident ${
                    counts.bulkAcceptable === 1 ? 'one' : 'ones'
                  }`}
            </WorkspaceButton>
          ) : null}
        </AiSurface>

        {failedWindows > 0 ? (
          <WorkspaceNotice tone="at-risk">
            Part of the document could not be read ({plural(failedWindows, 'section')} of{' '}
            {(run.counts.windows ?? failedWindows).toLocaleString()}), so this list is incomplete.
            Read the document again from Document imports to try the rest.
          </WorkspaceNotice>
        ) : null}
        {run.duplicate_check_mode === 'word_overlap' ? (
          <p className="text-meta text-ink-500">
            Duplicates were checked by wording only for this import: requirements that say the same
            thing in different words may not be flagged.
          </p>
        ) : null}
        {reviewError ? <WorkspaceNotice tone="critical">{reviewError}</WorkspaceNotice> : null}

        <div className="flex flex-wrap items-center gap-2">
          <FilterChips
            aria-label="Show candidates"
            options={filterOptions}
            value={filter}
            onChange={setFilter}
          />
          {inReview ? (
            <WorkspaceButton
              variant="tertiary"
              className="ml-auto"
              disabled={confirming}
              onClick={() => setDiscardOpen(true)}
            >
              Discard this import
            </WorkspaceButton>
          ) : null}
        </div>

        {shown.length === 0 ? (
          <p className="rounded-card border border-ink-100 bg-paper-0 px-4 py-8 text-center text-body-sm text-ink-500">
            No candidates in this view.
          </p>
        ) : (
          shown.map((c) => (
            <IntakeCandidateCard
              key={c.id}
              candidate={c}
              fileName={run.document.file_name}
              modules={moduleOptions}
              subcategories={
                c.module_id
                  ? (subcategoriesFor?.(c.module_id) ?? NO_SUBCATEGORIES)
                  : NO_SUBCATEGORIES
              }
              saving={saving.has(c.id) || confirming}
              readOnly={!inReview}
              error={cardErrors.get(c.id) ?? null}
              flagged={flaggedId === c.id}
              onDecide={(decision) => change(c, { decision })}
              onModule={(moduleId) => {
                if (moduleId !== c.module_id) change(c, { moduleId });
              }}
              onSubcategory={(subcategory) => change(c, { subcategory })}
              onOpenSource={(citationIndex) =>
                setSource({ runId: run.id, candidateId: c.id, citationIndex })
              }
            />
          ))
        )}
      </>
    );
  }

  const busy = confirming || view.kind === 'uploading';

  return (
    <>
      <Sheet
        open
        onOpenChange={(next) => {
          if (!next && !confirming) onClose();
        }}
        closeOnScrimClick={!inReview && view.kind !== 'uploading'}
        onEscapeKeyDown={(e) => {
          // Esc closes an open module list first, not the whole sheet.
          if (document.querySelector('[data-import-popup-open]')) e.preventDefault();
        }}
        title="Import from a document"
        description="AI reads the whole document and proposes requirements. You decide what goes into the library."
        headerActions={
          onOpenRuns ? (
            <WorkspaceButton disabled={busy} onClick={onOpenRuns}>
              Document imports
            </WorkspaceButton>
          ) : undefined
        }
        summary={
          inReview ? (
            <>
              <SheetStat value={counts.accepted.toLocaleString()} label="accepted" />
              <SheetStat value={counts.skipped.toLocaleString()} label="skipped" />
              <SheetStat value={counts.undecided.toLocaleString()} label="undecided" />
            </>
          ) : undefined
        }
        actions={
          <>
            <WorkspaceButton size="md" disabled={confirming} onClick={onClose}>
              {view.kind === 'drop' ? 'Cancel' : 'Close'}
            </WorkspaceButton>
            {inReview ? (
              <WorkspaceButton
                size="md"
                variant="primary"
                disabled={counts.accepted === 0 || confirming || saving.size > 0}
                onClick={confirm}
              >
                {confirming
                  ? 'Adding…'
                  : counts.accepted > 0
                    ? `Add ${counts.accepted.toLocaleString()} to drafts`
                    : 'Accept at least one'}
              </WorkspaceButton>
            ) : null}
          </>
        }
      >
        <div
          data-testid="intake-body"
          onDragOver={(e) => {
            e.preventDefault();
            if (view.kind === 'drop') setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto bg-paper-50 p-5 [&>*]:shrink-0"
        >
          <input
            ref={inputRef}
            type="file"
            accept={INTAKE_ACCEPT}
            aria-label="Choose a document"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) upload(file);
            }}
          />
          {body}
        </div>
      </Sheet>

      <WorkspaceDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Discard this import?"
        description="Nothing has been added to any draft."
        actions={
          <>
            <WorkspaceButton size="md" onClick={() => setDiscardOpen(false)}>
              Keep reviewing
            </WorkspaceButton>
            <WorkspaceButton
              size="md"
              variant="destructive"
              disabled={cancelMutation.isPending}
              onClick={cancelRun}
            >
              {cancelMutation.isPending ? 'Discarding…' : 'Discard the import'}
            </WorkspaceButton>
          </>
        }
      >
        <p className="text-body-sm text-ink-700">
          The decisions made so far are dropped and the import is closed as cancelled. The document
          stays in the firm library and can be read again from Document imports.
        </p>
      </WorkspaceDialog>

      <IntakeSourceViewer target={source} onClose={() => setSource(null)} />
    </>
  );
}
